import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, RefreshCw, Pencil, Coins, XCircle, Unlock, Megaphone } from 'lucide-react';

const fmt = (n, d = 4) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: d });

const EMPTY_POOL = {
  pool_id: '', symbol: '', type: 'flexible', apy: '', min_amount: '',
  duration_days: '', name: '', enabled: true, announcement: '',
};

function PoolForm({ initial, onSave, onCancel, busy }) {
  const [draft, setDraft] = useState({ ...EMPTY_POOL, ...initial });
  const isEdit = !!initial?.pool_id;
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Symbol</label>
          <Input value={draft.symbol} disabled={isEdit} onChange={e => set('symbol', e.target.value.toUpperCase())}
            className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]"
            placeholder="BTC" data-testid="pool-form-symbol" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Type</label>
          <div className="mt-1 inline-flex border border-[#2B3139] p-0.5 h-9 w-full">
            {['flexible', 'fixed'].map(t => (
              <button key={t} type="button" disabled={isEdit}
                onClick={() => set('type', t)}
                className={`flex-1 text-xs uppercase font-mono ${draft.type === t ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#B7BDC6] hover:text-white'} ${isEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid={`pool-form-type-${t}`}
              >{t}</button>
            ))}
          </div>
        </div>
        <div className="col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Display Name</label>
          <Input value={draft.name} onChange={e => set('name', e.target.value)}
            className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white focus-visible:border-[#FCD535]"
            placeholder="e.g. Bitcoin Flexible" data-testid="pool-form-name" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">APY (%)</label>
          <Input type="number" step="0.1" value={draft.apy} onChange={e => set('apy', e.target.value)}
            className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]"
            placeholder="5.0" data-testid="pool-form-apy" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Min Amount</label>
          <Input type="number" step="any" value={draft.min_amount} onChange={e => set('min_amount', e.target.value)}
            className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]"
            placeholder="0.01" data-testid="pool-form-min" />
        </div>
        {draft.type === 'fixed' && (
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Lock duration (days)</label>
            <Input type="number" min="1" step="1" value={draft.duration_days} onChange={e => set('duration_days', e.target.value)}
              className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]"
              placeholder="30" data-testid="pool-form-duration" />
          </div>
        )}
        <div className="col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Enabled</label>
          <div className="mt-1">
            <button type="button" onClick={() => set('enabled', !draft.enabled)}
              className={`px-3 h-9 text-xs font-mono uppercase tracking-wider border ${draft.enabled ? 'bg-[#0ECB81]/10 border-[#0ECB81]/40 text-[#0ECB81]' : 'bg-[#F6465D]/10 border-[#F6465D]/40 text-[#F6465D]'}`}
              data-testid="pool-form-enabled"
            >
              {draft.enabled ? 'Enabled — visible to users' : 'Disabled — hidden from users'}
            </button>
          </div>
        </div>
        <div className="col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-[#848E9C] flex items-center gap-1">
            <Megaphone className="w-3 h-3" /> Announcement (optional · shows on pool card)
          </label>
          <textarea
            value={draft.announcement || ''}
            onChange={e => set('announcement', e.target.value.slice(0, 500))}
            rows={3}
            maxLength={500}
            placeholder="e.g. First 48h only — 150% APY boost! Ends Fri 9pm UTC."
            className="mt-1 w-full bg-[#0B0E11] border border-[#2B3139] rounded-sm text-white text-sm p-2 focus:outline-none focus:border-[#FCD535] resize-none"
            data-testid="pool-form-announcement"
          />
          <div className="text-[10px] text-[#848E9C] mt-1 text-right">{(draft.announcement || '').length}/500 · Leave blank to remove.</div>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={onCancel} className="bg-transparent border border-[#2B3139] hover:bg-[#2B3139] text-white rounded-sm">Cancel</Button>
        <Button onClick={() => onSave(draft)} disabled={busy}
          className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold"
          data-testid="pool-form-save"
        >
          {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create pool'}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function StakingPoolsAdminPanel() {
  const [tab, setTab] = useState('pools');
  const [pools, setPools] = useState([]);
  const [stakes, setStakes] = useState([]);
  const [stakeStatusFilter, setStakeStatusFilter] = useState('active');
  const [editorPool, setEditorPool] = useState(null); // null = closed, {} = create, {…} = edit
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmClose, setConfirmClose] = useState(null);
  const [announceEditor, setAnnounceEditor] = useState(null); // {pool, text}
  const [busy, setBusy] = useState(false);

  const loadPools = async () => {
    try {
      const r = await api.get('/admin/staking/pools');
      setPools(r.data.pools || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load pools'); }
  };
  const loadStakes = async () => {
    try {
      const q = stakeStatusFilter ? `?status=${stakeStatusFilter}&limit=500` : '?limit=500';
      const r = await api.get(`/admin/staking/stakes${q}`);
      setStakes(r.data.stakes || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load stakes'); }
  };
  useEffect(() => { loadPools(); }, []);
  useEffect(() => { if (tab === 'stakes') loadStakes(); }, [tab, stakeStatusFilter]); // eslint-disable-line

  const savePool = async (draft) => {
    setBusy(true);
    try {
      const body = {
        symbol: draft.symbol,
        type: draft.type,
        apy: parseFloat(draft.apy),
        min_amount: parseFloat(draft.min_amount),
        duration_days: parseInt(draft.duration_days || 0, 10),
        name: draft.name,
        enabled: !!draft.enabled,
      };
      if (editorPool?.pool_id) {
        // Update — omit symbol/type/duration for flex
        const upd = { apy: body.apy, min_amount: body.min_amount, name: body.name, enabled: body.enabled, announcement: draft.announcement || '' };
        if (draft.type === 'fixed') upd.duration_days = body.duration_days;
        await api.put(`/admin/staking/pools/${editorPool.pool_id}`, upd);
        toast.success(`Pool ${editorPool.pool_id} updated`);
      } else {
        body.announcement = draft.announcement || '';
        await api.post('/admin/staking/pools', body);
        toast.success(`Created pool ${body.symbol}`);
      }
      setEditorPool(null);
      loadPools();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const deletePool = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await api.delete(`/admin/staking/pools/${confirmDelete.pool_id}`);
      toast.success(`Deleted ${confirmDelete.pool_id}`);
      setConfirmDelete(null);
      loadPools();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  const toggleEnabled = async (pool) => {
    try {
      await api.put(`/admin/staking/pools/${pool.pool_id}`, { enabled: !pool.enabled });
      toast.success(`${pool.pool_id} ${!pool.enabled ? 'enabled' : 'disabled'}`);
      loadPools();
    } catch (e) { toast.error(e.response?.data?.detail || 'Toggle failed'); }
  };

  const forceClose = async () => {
    if (!confirmClose) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/staking/stakes/${confirmClose.stake_id}/force-close`);
      toast.success(`Refunded ${r.data.refunded.toFixed(6)} ${confirmClose.symbol}`);
      setConfirmClose(null);
      loadStakes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Close failed');
    } finally { setBusy(false); }
  };

  const saveAnnouncement = async () => {
    if (!announceEditor) return;
    setBusy(true);
    try {
      await api.put(`/admin/staking/pools/${announceEditor.pool.pool_id}`, {
        announcement: announceEditor.text || '',
      });
      toast.success(
        announceEditor.text?.trim()
          ? `Announcement live on ${announceEditor.pool.symbol}`
          : `Announcement cleared on ${announceEditor.pool.symbol}`
      );
      setAnnounceEditor(null);
      loadPools();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-staking-panel">
      {/* Sub-tabs */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex border border-[#2B3139] p-0.5">
          {[['pools', 'Pools'], ['stakes', 'User Stakes']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-1 text-[10px] uppercase font-mono tracking-wider ${tab === k ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#B7BDC6] hover:text-white'}`}
              data-testid={`staking-admin-tab-${k}`}
            >{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => { loadPools(); if (tab === 'stakes') loadStakes(); }}
            className="bg-[#2B3139] hover:bg-[#3A424C] text-white rounded-sm h-8 text-xs"
            data-testid="staking-admin-refresh"
          ><RefreshCw className="w-3 h-3 mr-1.5" /> Refresh</Button>
          {tab === 'pools' && (
            <Button onClick={() => setEditorPool({})}
              className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm h-8 text-xs"
              data-testid="staking-admin-new-pool"
            ><Plus className="w-3 h-3 mr-1.5" /> New Pool</Button>
          )}
        </div>
      </div>

      {tab === 'pools' && (
        <div className="space-y-4">
          {/* Announcement quick-manager — dedicated card so it's discoverable */}
          <div className="border border-[#FCD535]/30 bg-gradient-to-br from-[#FCD535]/5 to-transparent p-4" data-testid="announcement-manager">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Megaphone className="w-4 h-4 text-[#FCD535]" />
                <div className="text-white text-sm font-semibold">Pool Announcements</div>
                <span className="text-[10px] text-[#848E9C]">Push a message to users' "What's New" banner on the Staking page.</span>
              </div>
              <div className="text-[10px] text-[#848E9C]">
                <span className="font-mono text-[#FCD535]">{pools.filter(p => p.announcement).length}</span> live · <span className="font-mono">{pools.length}</span> pools total
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2">
              {pools.map(p => (
                <div key={p.pool_id} className="bg-[#0B0E11] border border-[#2B3139] p-3 flex items-start gap-3" data-testid={`announcement-row-${p.pool_id}`}>
                  <div className="flex-shrink-0 w-9 h-9 bg-[#181A20] border border-[#2B3139] flex items-center justify-center">
                    <Coins className="w-4 h-4 text-[#FCD535]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-white text-xs font-semibold">{p.symbol}</span>
                      <span className="text-[10px] text-[#848E9C]">{p.name}</span>
                      {p.announcement && (
                        <span className="text-[9px] px-1 py-0.5 bg-[#FCD535]/15 text-[#FCD535] uppercase tracking-wider">live</span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#B7BDC6] truncate" title={p.announcement || ''}>
                      {p.announcement || <span className="text-[#848E9C] italic">No announcement</span>}
                    </div>
                    {p.announcement_updated_at && (
                      <div className="text-[9px] text-[#848E9C] mt-0.5 font-mono">
                        Updated {new Date(p.announcement_updated_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setAnnounceEditor({ pool: p, text: p.announcement || '' })}
                    className="flex-shrink-0 px-2 py-1 text-[10px] uppercase font-mono tracking-wider bg-[#FCD535]/10 hover:bg-[#FCD535]/20 text-[#FCD535] border border-[#FCD535]/30"
                    data-testid={`announcement-edit-${p.pool_id}`}
                    title="Edit announcement"
                  >
                    {p.announcement ? 'Edit' : 'Set'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-[#2B3139] bg-[#181A20]">
          <div className="hidden md:grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
            <div className="col-span-3">Name</div>
            <div className="col-span-1">Symbol</div>
            <div className="col-span-1">Type</div>
            <div className="col-span-1">APY</div>
            <div className="col-span-1">Min</div>
            <div className="col-span-1">Lock</div>
            <div className="col-span-1">Active</div>
            <div className="col-span-1">Staked</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-1 text-right">Action</div>
          </div>
          {pools.length === 0 ? (
            <div className="p-6 text-center text-[#848E9C] text-sm">No pools yet.</div>
          ) : pools.map(p => (
            <div key={p.pool_id} className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-3 border-b border-[#2B3139]/40 items-center text-xs" data-testid={`pool-row-${p.pool_id}`}>
              <div className="md:col-span-3 text-white">
                <div className="font-semibold flex items-center gap-1.5">
                  {p.name}
                  {p.announcement && (
                    <span title={p.announcement} className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 bg-[#FCD535]/15 text-[#FCD535] uppercase tracking-wider">
                      <Megaphone className="w-2.5 h-2.5" /> live
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[#848E9C] font-mono">{p.pool_id}</div>
              </div>
              <div className="md:col-span-1 font-mono text-[#FCD535] flex items-center gap-1"><Coins className="w-3 h-3" />{p.symbol}</div>
              <div className="md:col-span-1 capitalize text-[#B7BDC6]">{p.type}</div>
              <div className="md:col-span-1 font-mono text-[#0ECB81]">{p.apy}%</div>
              <div className="md:col-span-1 font-mono text-white">{fmt(p.min_amount, 6)}</div>
              <div className="md:col-span-1 font-mono text-white">{p.duration_days ? `${p.duration_days}d` : 'flex'}</div>
              <div className="md:col-span-1 font-mono text-white">{p.active_stakes ?? 0}</div>
              <div className="md:col-span-1 font-mono text-white">{fmt(p.total_staked, 4)}</div>
              <div className="md:col-span-1">
                <button onClick={() => toggleEnabled(p)}
                  className={`px-1.5 py-0.5 text-[9px] uppercase tracking-wider hover:opacity-80 ${p.enabled ? 'bg-[#0ECB81]/10 text-[#0ECB81]' : 'bg-[#F6465D]/10 text-[#F6465D]'}`}
                  data-testid={`pool-toggle-${p.pool_id}`}
                >{p.enabled ? 'live' : 'off'}</button>
              </div>
              <div className="md:col-span-1 text-right flex items-center justify-end gap-1">
                <button onClick={() => setEditorPool(p)} className="p-1 text-[#B7BDC6] hover:text-[#FCD535]" title="Edit" data-testid={`pool-edit-${p.pool_id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setConfirmDelete(p)} className="p-1 text-[#B7BDC6] hover:text-[#F6465D]" title="Delete" data-testid={`pool-delete-${p.pool_id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
          </div>
        </div>
      )}

      {tab === 'stakes' && (
        <div className="border border-[#2B3139] bg-[#181A20]">
          <div className="px-4 py-3 border-b border-[#2B3139] flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-[#848E9C]">Filter:</span>
            <div className="inline-flex border border-[#2B3139] p-0.5">
              {['active', 'completed', ''].map(s => (
                <button key={s || 'all'} onClick={() => setStakeStatusFilter(s)}
                  className={`px-3 py-1 text-[10px] uppercase font-mono tracking-wider ${stakeStatusFilter === s ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#B7BDC6] hover:text-white'}`}
                  data-testid={`stake-filter-${s || 'all'}`}
                >{s || 'all'}</button>
              ))}
            </div>
          </div>
          <div className="hidden md:grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]/60">
            <div className="col-span-3">User</div>
            <div className="col-span-1">Symbol</div>
            <div className="col-span-2">Pool</div>
            <div className="col-span-1">Amount</div>
            <div className="col-span-1">APY</div>
            <div className="col-span-1">Rewards</div>
            <div className="col-span-2">End at</div>
            <div className="col-span-1 text-right">Action</div>
          </div>
          {stakes.length === 0 ? (
            <div className="p-6 text-center text-[#848E9C] text-sm">No stakes.</div>
          ) : stakes.map(s => (
            <div key={s.stake_id} className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-3 border-b border-[#2B3139]/40 items-center text-xs" data-testid={`stake-row-${s.stake_id}`}>
              <div className="md:col-span-3 text-white truncate">
                <div className="truncate">{s.email || s.user_id}</div>
                <div className="text-[10px] text-[#848E9C] truncate">{s.stake_id}</div>
              </div>
              <div className="md:col-span-1 font-mono text-[#FCD535]">{s.symbol}</div>
              <div className="md:col-span-2 text-[#B7BDC6] truncate">{s.pool_id} <span className="text-[#848E9C]">({s.type})</span></div>
              <div className="md:col-span-1 font-mono text-white">{fmt(s.amount, 6)}</div>
              <div className="md:col-span-1 font-mono text-[#0ECB81]">{s.apy}%</div>
              <div className="md:col-span-1 font-mono text-[#0ECB81]">+{fmt(s.rewards_earned, 6)}</div>
              <div className="md:col-span-2 text-[#B7BDC6]">
                {s.end_at ? new Date(s.end_at).toLocaleDateString() : <span className="text-[#848E9C]">flexible</span>}
                {s.is_matured && s.status === 'active' && <span className="ml-1 text-[9px] px-1 py-0.5 bg-[#0ECB81]/10 text-[#0ECB81]">matured</span>}
              </div>
              <div className="md:col-span-1 text-right">
                {s.status === 'active' ? (
                  <Button onClick={() => setConfirmClose(s)} size="sm"
                    className="bg-[#2B3139] hover:bg-[#3A424C] text-[#FCD535] rounded-sm h-6 text-[10px] px-2"
                    data-testid={`stake-close-${s.stake_id}`}
                  ><Unlock className="w-3 h-3 mr-1" /> Force close</Button>
                ) : (
                  <span className="text-[10px] text-[#848E9C] uppercase">{s.status}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pool editor dialog */}
      <Dialog open={!!editorPool} onOpenChange={(o) => !o && setEditorPool(null)}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle>{editorPool?.pool_id ? `Edit pool · ${editorPool.pool_id}` : 'Create new staking pool'}</DialogTitle>
          </DialogHeader>
          {editorPool && (
            <PoolForm initial={editorPool} onSave={savePool} onCancel={() => setEditorPool(null)} busy={busy} />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete pool confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><XCircle className="w-5 h-5 text-[#F6465D]" />Delete pool?</DialogTitle></DialogHeader>
          <div className="text-sm text-[#B7BDC6] space-y-2">
            <p>Delete <span className="font-mono text-white">{confirmDelete?.pool_id}</span> ({confirmDelete?.symbol})?</p>
            <p className="text-[10px] text-[#848E9C]">This only works if there are 0 active stakes. If active stakes exist, disable the pool instead so existing users can still unstake.</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setConfirmDelete(null)} className="bg-transparent border border-[#2B3139] hover:bg-[#2B3139] text-white rounded-sm">Cancel</Button>
            <Button onClick={deletePool} disabled={busy} className="bg-[#F6465D] hover:bg-[#e13c50] text-white rounded-sm font-semibold" data-testid="pool-delete-confirm">
              {busy ? 'Deleting…' : 'Yes, delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force-close stake confirm */}
      <Dialog open={!!confirmClose} onOpenChange={(o) => !o && setConfirmClose(null)}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Force-close stake?</DialogTitle></DialogHeader>
          <div className="text-sm text-[#B7BDC6] space-y-2">
            <p>Close stake <span className="font-mono text-[#FCD535]">{confirmClose?.stake_id}</span> for <span className="text-white">{confirmClose?.email}</span> early?</p>
            <p>User will be refunded <span className="font-mono text-white">{fmt(confirmClose?.amount, 6)} {confirmClose?.symbol}</span> principal plus <span className="font-mono text-[#0ECB81]">+{fmt(confirmClose?.rewards_earned, 6)} {confirmClose?.symbol}</span> accrued rewards.</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setConfirmClose(null)} className="bg-transparent border border-[#2B3139] hover:bg-[#2B3139] text-white rounded-sm">Cancel</Button>
            <Button onClick={forceClose} disabled={busy} className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold" data-testid="stake-close-confirm">
              {busy ? 'Closing…' : 'Yes, close & refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Announcement-only editor */}
      <Dialog open={!!announceEditor} onOpenChange={(o) => !o && setAnnounceEditor(null)}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-[#FCD535]" />
              Announcement · {announceEditor?.pool?.symbol}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-[#848E9C]">
              This shows up in the yellow "What's New" banner on the top of /staking, and inline on the {announceEditor?.pool?.name} card.
            </div>
            <textarea
              value={announceEditor?.text || ''}
              onChange={e => setAnnounceEditor({ ...announceEditor, text: e.target.value.slice(0, 500) })}
              rows={4}
              maxLength={500}
              placeholder="e.g. 🚀 Boost weekend — 150% APY for 48h. Grab your slot!"
              className="w-full bg-[#0B0E11] border border-[#2B3139] rounded-sm text-white text-sm p-3 focus:outline-none focus:border-[#FCD535] resize-none"
              data-testid="announcement-only-textarea"
              autoFocus
            />
            <div className="flex items-center justify-between text-[10px] text-[#848E9C]">
              <span>Leave blank and save to remove.</span>
              <span>{(announceEditor?.text || '').length}/500</span>
            </div>
            {announceEditor?.text?.trim() && (
              <div className="bg-[#FCD535]/10 border-l-2 border-[#FCD535] px-3 py-2 text-xs text-[#FCD535] leading-relaxed flex items-start gap-2">
                <Megaphone className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-[#848E9C] mb-0.5">Preview</div>
                  {announceEditor.text}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setAnnounceEditor(null)} className="bg-transparent border border-[#2B3139] hover:bg-[#2B3139] text-white rounded-sm">Cancel</Button>
            <Button onClick={saveAnnouncement} disabled={busy}
              className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold"
              data-testid="announcement-only-save">
              {busy ? 'Saving…' : (announceEditor?.text?.trim() ? 'Publish' : 'Clear announcement')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
